<template>
  <v-dialog
    class="content-dialog agency-dialog"
    v-model="agencyDialog"
    persistent
    :fullscreen="mobile"
  >
    <div v-if="!agency && loading" class="dialog-loading-container">
      <v-progress-circular indeterminate color="primary"></v-progress-circular>
    </div>
    <v-card v-if="agency" rounded="lg" height="1200" :loading="loading">
      <AgencyDialogHeader
        :agency="agency"
        :intercom-last-seen="intercomLastSeen"
        @close="closeDialog"
      />
      <v-divider></v-divider>
      <div class="content-dialog-card">
        <v-row>
          <v-col cols="12" sm="7" :order="mobile ? 2 : 1">
            <div class="content-dialog-data-wrapper">
              <AgencyCoreData :agency="agency" v-if="agency" />
              <v-divider class="my-4"></v-divider>
              <v-expansion-panels class="agency-dialog-expansion-panels" multiple>
                <v-expansion-panel v-if="agency">
                  <v-expansion-panel-title>Raw Data</v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <AgencyRawData :agency="agency" embedded />
                  </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel v-if="agency">
                  <v-expansion-panel-title>PortalBuilder Vorschau</v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <AgencyPagePreview :agency-id="agency._id" hide-header />
                  </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel v-if="stripeCustomer">
                  <v-expansion-panel-title>Stripe Customer</v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <AgencyStripeCustomerData :agency="agency" hide-header />
                  </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel v-if="agency">
                  <v-expansion-panel-title>Stripe</v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <AgencyStripeData :agency="agency" hide-header />
                  </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel v-if="agency">
                  <v-expansion-panel-title>Kunden</v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <AgencyCustomerData
                      :agency="agency"
                      :customers="customers"
                      hide-header
                    />
                  </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel v-if="subusers">
                  <v-expansion-panel-title>Subusers</v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <AgencySubusers hide-header />
                  </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel v-if="emailTemplates">
                  <v-expansion-panel-title>Email Templates</v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <AgencyEmailTemplates hide-header />
                  </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel v-if="emailConnections">
                  <v-expansion-panel-title>Email Connections</v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <AgencyEmailConnections hide-header />
                  </v-expansion-panel-text>
                </v-expansion-panel>
                <v-expansion-panel v-if="whatsAppConnectionStats || agency">
                  <v-expansion-panel-title>WhatsApp Verbindungen</v-expansion-panel-title>
                  <v-expansion-panel-text>
                    <AgencyWhatsAppConnections hide-header />
                  </v-expansion-panel-text>
                </v-expansion-panel>
              </v-expansion-panels>
            </div>
          </v-col>
          <v-col cols="12" sm="5" order="1">
            <div class="agency-dialog-action-wrapper">
              <div class="agency-dialog-action-buttons">
                <BetaAccessButton :agency="agency" v-if="agency" />
                <ChangeMailButton :agency="agency" v-if="agency" />
                <ChangeAmountSlots :agency="agency" v-if="agency" />
                <GrantTrial :agency="agency" v-if="agency" />
                <FlushCache v-if="agency" />
                <MergeAgencies :agency="agency" v-if="agency" />
                <DeleteAgency :agency="agency" v-if="agency" />
              </div>
              <AgencyLogs :agency="agency" :logData="logData" v-if="agency" />
              <div class="agency-dialog-action-buttons">
                <CancelSubscription :agency="agency" v-if="agency" />
              </div>
            </div>
          </v-col>
        </v-row>
      </div>
    </v-card>
  </v-dialog>
</template>

<script>
import { mapState, mapActions, mapMutations } from "vuex";
import AgencyDialogHeader from "./agencyDialogData/AgencyDialogHeader.vue";
import AgencyRawData from "./agencyDialogData/AgencyRawData.vue";
import AgencyCoreData from "./agencyDialogData/AgencyCoreData.vue";
import AgencyStripeData from "./agencyDialogData/AgencyStripeData.vue";
import AgencyStripeCustomerData from "./agencyDialogData/AgencyStripeCustomerData.vue";
import AgencyCustomerData from "./agencyDialogData/AgencyCustomerData.vue";
import AgencySubusers from "./agencyDialogData/AgencySubusers.vue";
import AgencyEmailTemplates from "./agencyDialogData/AgencyEmailTemplates.vue";
import AgencyEmailConnections from "./agencyDialogData/AgencyEmailConnections.vue";
import AgencyWhatsAppConnections from "./agencyDialogData/AgencyWhatsAppConnections.vue";
import AgencyPagePreview from "./agencyDialogData/AgencyPagePreview.vue";
import BetaAccessButton from "./agencyDialogActions/BetaAccessButton.vue";
import AgencyLogs from "./agencyDialogActions/AgencyLogs.vue";
import ChangeMailButton from "./agencyDialogActions/ChangeMailButton.vue";
import ChangeAmountSlots from "./agencyDialogActions/ChangeAmountSlots.vue";
import GrantTrial from "./agencyDialogActions/GrantTrial.vue";
import CancelSubscription from "./agencyDialogActions/CancelSubscription.vue";
import FlushCache from "./agencyDialogActions/FlushCache.vue";
import MergeAgencies from "./agencyDialogActions/MergeAgencies.vue";
import DeleteAgency from "./agencyDialogActions/DeleteAgency.vue";
import { useDisplay } from "vuetify";
import apiClient from "@/plugins/axios";

export default {
  components: {
    AgencyDialogHeader,
    AgencyRawData,
    AgencyCoreData,
    AgencyStripeData,
    AgencyCustomerData,
    AgencySubusers,
    AgencyLogs,
    BetaAccessButton,
    ChangeMailButton,
    ChangeAmountSlots,
    GrantTrial,
    AgencyEmailTemplates,
    AgencyEmailConnections,
    AgencyWhatsAppConnections,
    AgencyPagePreview,
    AgencyStripeCustomerData,
    CancelSubscription,
    FlushCache,
    MergeAgencies,
    DeleteAgency,
  },
  setup() {
    const { mobile } = useDisplay();
    return { mobile };
  },
  data() {
    return {
      intercomLastSeen: null,
    };
  },
  props: {
    agencyId: {
      type: String,
      required: true,
    },
  },
  computed: {
    ...mapState("agency", [
      "agency",
      "logData",
      "loading",
      "customers",
      "agencyDialog",
      "subusers",
      "emailTemplates",
      "emailConnections",
      "whatsAppConnectionStats",
      "stripeCustomer",
    ]),
  },
  watch: {
    agencyId: {
      handler(newId, oldId) {
        if (newId !== oldId) {
          this.fetchData(newId);
        }
      },
      immediate: true,
    },
    agency: {
      handler(newAgency) {
        if (newAgency && newAgency.email) {
          this.fetchIntercomLastSeen(newAgency.email);
        } else {
          this.intercomLastSeen = null;
        }
      },
      immediate: false,
    },
  },
  methods: {
    ...mapActions("agency", [
      "fetchAgencyData",
      "closeAgencyDialog",
      "resetAgencyDialog",
    ]),
    ...mapMutations("agency", ["setAgencyDialog"]),
    async fetchIntercomLastSeen(email) {
      try {
        const resp = await apiClient.post("/intercom/getContactLastSeen", {
          email,
        });
        this.intercomLastSeen = resp.data?.last_seen_at || null;
      } catch (e) {
        this.intercomLastSeen = null;
        // Silent fail; not critical
      }
    },
    fetchData(agencyId) {
      this.fetchAgencyData(agencyId);
      this.setAgencyDialog(true);
    },
    closeDialog() {
      this.$store.dispatch("agency/closeAgencyDialog");
    },
  },
  beforeUnmount() {
    this.$store.dispatch("agency/resetAgencyDialog");
  },
};
</script>

<style lang="scss">
//THIS IS NOT SCOPED

.agency-dialog.content-dialog {
  max-width: 1800px;
}

.agency-dialog-action-wrapper {
  margin: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: sticky;
  top: 12px;

  @media screen and (max-width: 600px) {
    position: relative;
    top: 0;
  }
}

.agency-dialog-action-buttons {
  margin-top: 20px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background-color: #f9fafb;
  border-radius: 12px;
  box-shadow: 0 0 8px 0 rgba(0, 0, 0, 0.2);

  @media screen and (max-width: 600px) {
    gap: 24px;
    padding: 8px;
  }
}

.agency-dialog-expansion-panels {
  margin-top: 8px;

  .data-wrapper-core,
  .content-raw-data-wrapper {
    box-shadow: none;
    border: none;
  }
}
</style>
